<template lang="pug">
v-container.pa-8(fluid style="max-width:1600px")
  v-row.pb-2.pb-md-0(no-gutters)
    v-col(cols="12" md="8")
      v-tabs(v-model="tabs" color="basic700" :height="44" grow show-arrows active-class="font-semibold" @change="tabChanged")
        v-tab.text-sm.font-medium.text-none(v-for="status in statuses" :key="status.value") {{ $t(status) }} {{ getStatCount(status) }}
    v-col(cols="12" md="4" align="end")
      .d-flex.align-center
        v-spacer
        v-btn(@click="clickSearch" icon color="neutral500")
          v-icon(size="20") $yzi-search-sm
        v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
  v-divider

  .data-table
    v-data-table.table-custom.mt-sm-4.pb-6(:headers="headers" :items="patients" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" disable-sort :header-props="{ sortIcon: '$yzi-triangle' }" @click:row="onSelectConsultation" loader-height="2")
      //- patient
      template(v-slot:item.patient="{ item, index }")
        div(:id="`pt-${index}`" style="position: relative;")
        div.d-flex.my-3
          div(style="width: 30px;")
            v-responsive(:aspect-ratio="7/10" style="width: 30px;")
              v-card.fill-height(v-if="item.patient.profile_image && item.patient.profile_image.file" flat)
                v-img(:src="`https://img.yezza.io/50x50/${item.patient.profile_image.path}`" height="100%" width="100%")
                  template(v-slot:placeholder)
                    v-row(class="fill-height ma-0" align="center" justify="center")
                      v-progress-circular(indeterminate color="grey")
              v-card.neutral300.fill-height(v-else flat)
                div.d-flex.align-center.justify-center.fill-height
                  v-icon(size="20") $yzi-user-03
          div.pl-2
            v-tooltip(top color="black" :attach="`#pt-${index}`" content-class="black tooltip-top-arrow")
              template(#activator="{ on, attrs }")
                div(v-bind="attrs" v-on="on")
                  .text-sm.font-semibold.neutral700--text.ellipsis-text-1 {{ item.patient.name }}
              div(style="max-width: 500px;")
                .text-sm.font-semibold {{ item.patient.name }}
                .text-xs(v-if="item.patient.dob") {{ $options.filters.getAgeByYearMonthDay(item.patient.dob) | displayAgeByYearMonthDay }} old
            div.d-flex.align-start.flex-column
              y-chip(v-if="item.urgent" :label="$t('urgent')" size="xs" color="red")
              .text-xs.neutral700--text.no-wrap(v-if="item.patient.dob") {{ $options.filters.getAgeByYearMonthDay(item.patient.dob) | displayAgeByYearMonthDay }} old
      //- queue
      template(v-slot:item.queue="{ item }")
        .text-sm.font-semibold.neutral700--text {{ item.queue_no }}
      //- arrive at
      template(v-slot:item.arrive="{ item }")
        .text-xs.font-semibold.neutral700--text {{ item.waiting_at | humanizeDate }}
        .text-xs.neutral700--text {{ item.waiting_at | displayTime2 }}
      //- visit notes
      template(v-slot:item.remarks="{ item, index }")
        div(:id="`rm-${index}`" style="position: relative;")
        div.my-3
          v-tooltip(top color="black" :attach="`#rm-${index}`" content-class="black tooltip-top-arrow")
            template(#activator="{ on, attrs }")
              div(v-bind="attrs" v-on="on" style="max-width: 175px;")
                .text-xs.neutral700--text.cursor-help.ellipsis-text-2 {{ item.remarks }}
            div.text-xs(style="max-width: 500px;") {{ item.remarks }}
      //- doctor
      template(v-slot:item.provider="{ item, index }")
        div(:id="`pv-${index}`" style="position: relative;")
        div.my-3(v-if="item.provider")
          v-tooltip(top color="black" :attach="`#pv-${index}`" content-class="black tooltip-top-arrow")
            template(#activator="{ on, attrs }")
              div(v-bind="attrs" v-on="on")
                y-chip.cursor-help(v-bind="attrs" v-on="on" :label="getProviderName(item.provider.name)" size="sm" chip-class="pa-1" outlined)
                  template(#prepend)
                    v-avatar.mr-1(size="20" color="neutral300")
                      template(v-if="item.provider.profile_image")
                        v-img(:src="`https://img.yezza.io/50x50/${item.provider.profile_image.path}`")
                      v-icon(v-else color="neutral500" size="15") $yzi-user-03
            div {{ item.provider.name }}
      //- payment
      template(v-slot:item.payment="{ item, index }")
        div(:id="`pm-${index}`" style="position: relative;")
        div.my-3(v-if="item.default_payment")
          v-tooltip(top color="black" :attach="`#pm-${index}`" content-class="black tooltip-top-arrow")
            template(#activator="{ on, attrs }")
              div(v-bind="attrs" v-on="on")
                div(style="max-width: 140px;")
                  .text-sm.font-semibold.neutral700--text.cursor-help.truncate {{ item.default_payment.type === 'panel' ? item.default_payment.panel.name : 'Self-pay' }}
                  .text-xs.neutral700--text.cursor-help.truncate {{ item.default_payment.note }}
            div(style="max-width: 30vw")
              .text-sm.font-semibold {{ item.default_payment.type === 'panel' ? item.default_payment.panel.name : 'Self-pay' }}
              .text-xs {{ item.default_payment.note }}
      //- duration
      template(v-slot:item.duration="{ item }")
        y-chip(:label="getDuration(item)" size="sm" chip-class="pa-1" outlined)
          template(#prepend)
            v-icon.mr-1(size=16) mdi-clock-outline
      //- status
      template(v-slot:item.status="{ item }")
        y-chip.no-wrap(v-if="disableCallTo(item)" label="Incoming" color="normal")
        y-chip.no-wrap(v-else :label="getStatus(item)" :color="getStatusColor(item.status)")
      //- actions
      template(v-slot:item.actions="{ item }")
        div.d-flex.align-center
          y-button.mr-3(label="View" outlined color="neutral400" text-color="neutral700" size="xxs" unset-min-width button-class="px-2")
          v-menu(v-if="actionableStatuses.includes(item.status) && !disableCallTo(item)" offset-y left rounded="lg")
            template(v-slot:activator="{ on }")
              div(v-on="on")
                y-button(label="Call in" size="xxs" unset-min-width button-class="px-2")
            v-list.py-0
              template(v-if="seats.length > 0")
                template(v-for="(seat, seatIdx) in seats")
                  v-divider(v-if="seats !== 0")
                  v-list-item(@click="onCallPatientIn(item, seat)" :key="'action-' + seatIdx")
                    v-list-item-title.text-sm.basic800--text {{ $t('call_to') }} {{ seat.name }}
              template(v-else)
                v-list-item
                  v-list-item-title.text-sm.neutral500--text.italic {{ $t('no_list_available') }}
    div.d-flex.align-center(v-if="totalCount > pageSize")
      v-spacer
      div
        yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")

</template>

<script>
import { DateTime } from 'luxon'
import { mapState, mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import bell from '@/assets/audio/bell_announcement.mp3'

export default {
  name: "Consultation",
  components: { CustomizeColumnMenu },
  props: ['savePage', 'saveTab', 'saveSearch'],
  data: () => ({
    showSearchField: false,
    isFetching: false,
    allowRefetch: false,
    searchText: null,
    totalCount: 0,
    pageSize: 20,
    currentPage: 1,
    patients: [],
    tabs: 0,
    statuses: ["waiting", "serving", "on_hold", "dispensary", "completed", "all"],
    timer: null,
    currentTime: null,
    audio: null,
    stats: {},
    seats: [],
    actionableStatuses: ["waiting", "on_hold"],
    showAddFamilyDialog: true,
  }),
  computed: {
    ...mapState("user", ["member"]),
    ...mapGetters({
      company: "getCompany",
      registrationFields: "clinic/getRegistrationFields",
      isDoctor: "user/isDoctor",
    }),
    isMobileScreen() {
      return this.$vuetify.breakpoint.xs;
    },
    headers() {
      const name = { text: "Patient", value: "patient", enable: true };
      const fields = [...this.registrationFields];
      fields.map((item) => {
        item.text = item.field_name
        item.value = item.id
        switch (item.id) {
          case 'queue':
            item.width = 90
            break
          case 'arrive':
            item.width = 110
            break
          case 'provider':
            item.width = 160
            break
          case 'duration':
            item.width = 150
            break
        }
      });
      const actions = {
        text: "",
        value: "actions",
        enable: true,
        sortable: false,
      };
      return [name, ...fields, actions];
    },
  },
  watch: {
    // tabs(val) {
    //   const paramName = this.statuses[val].toLowerCase();
    //   this.$router
    //     .push({ query: { ...this.$route.query, tab: paramName } })
    //     .catch(() => {});
    //   if (val !== 5) {
    //     this.searchText = null;
    //   }
    //   this.currentPage = 1;
    //   this.fetchPatientlist();
    // },
    stats(val, oldVal) {
      if (oldVal && this.isDoctor) {
        if (val.waiting > oldVal.waiting) {
          this.playAudio();
        }
      }
    },
  },
  mounted() {
    this.allowRefetch = true;
    this.headers.map((item) => (item["class"] = "text-table-header"));
    this.currentTime = new Date().toISOString();
    this.checkRoute();
    this.fetchPatientlist();
    this.fetchAvailableSeats();
    this.initWsUpdate()
  },
  methods: {
    ...mapActions(["showMessage"]),
    fetchPatientlist(hideLoading) {
      this.clearTimer();
      if (!hideLoading) {
        this.isFetching = true;
      }
      let params = {
        ordering: "-urgent,id",
        codes: "1,2",
        page_size: this.pageSize,
        page: this.currentPage,
      };
      let status = this.statuses[this.tabs];
      if (status === "all") {
        params.ordering = "-id";
      } else if (status === "dispensary") {
        params.status = "checkout";
      } else if (status === "completed") {
        params.ordering = "-id";
        params.status = "completed";
      } else {
        params.status = status;
      }
      if (this.searchText) {
        params.search = this.searchText;
      } else if (status === "all" || status === "completed") {
        params.date__lte = this.getTodayDate();
        params.date__gte = this.getYesterdayDate();
      }
      this.fetchStats();
      this.$axios
        .get(`/clinics/${this.company.slug}/registrations`, { params })
        .then(({ data }) => {
          this.patients = data.results;
          this.totalCount = data.count;
          if (this.allowRefetch) {
            this.setTimer();
          }
        })
        .catch(() => {
          this.showMessage([
            "error",
            this.$t("error_message.unable_to_complete_request"),
          ]);
        })
        .finally(() => {
          this.isFetching = false;
        });
    },
    fetchAvailableSeats() {
      this.isFetchingSeats = true;
      let params = {
        status: "active",
        type: "room",
        fields: "id,name",
        page_size: -1,
      };
      this.$axios
        .get(`/clinics/${this.company.slug}/facilities`, { params })
        .then((resp) => {
          if (resp.data && resp.data.results) {
            this.seats = resp.data.results;
          }
        })
        .catch(() => {
          this.showMessage([
            "error",
            this.$t("error_message.unable_to_complete_request"),
          ]);
        })
        .finally(() => {
          this.isFetchingSeats = false;
        });
    },
    fetchStats() {
      this.$axios.get(`/clinics/${this.company.slug}/registrations/stats`, { params: { codes: "1,2" } })
        .then((resp) => {
          if (resp.data && resp.data.stats) {
            this.stats = resp.data.stats
          }
        })
        .catch(() => {});
    },
    pageChanged() {
      this.fetchPatientlist();
    },
    tabChanged () {
      if (this.tabs !== 5) {
        this.searchText = null
        this.blurSearch()
      }
      this.currentPage = 1
      this.fetchPatientlist()
    },
    initWsUpdate() {
      this.$wamp.subscribe(`clinic.registration.changed:${this.company.slug}`, () => {
        this.fetchPatientlist(true);
      })
    },
    updateTime() {
      this.currentTime = new Date().toISOString();
    },
    setTimer() {
      this.timer = setTimeout(this.updateTime, 5000);
    },
    clearTimer() {
      clearTimeout(this.timer);
    },
    checkRoute() {
      if (this.saveSearch) {
        this.searchText = this.saveSearch
        this.clickSearch()
      }
      if (this.savePage) {
        this.currentPage = this.savePage
      }
      if (this.saveTab) {
        this.tabs = this.saveTab
      }
    },
    getStatCount(status) {
      let text = "";
      let actualStatus = status;
      if (status === "dispensary") {
        actualStatus = "checkout";
      }
      if (this.stats[actualStatus] && status !== "all" && status !== "completed") {
        text = `(${this.stats[actualStatus]})`;
      }
      return text;
    },
    getProviderName(str) {
      if (str.length > 15) {
        return str.substring(0, 15 - 3) + "...";
      } else {
        return str;
      }
    },
    clickSearch() {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false;
        }
      } else {
        this.showSearchField = true;
      }
    },
    blurSearch() {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false;
      }
    },
    queryChanged: debounce(function () {
      this.tabs = 5;
      this.currentPage = 1;
      this.fetchPatientlist();
    }, 750),
    getStatusColor(status) {
      switch (status) {
        case "waiting":
          return "red";

        case "serving":
          return "orange";

        case "on_hold":
          return "grey";

        case "checkout":
          return "green";

        case "completed":
          return "blue";

        default:
          return "normal";
      }
    },
    onSelectConsultation(data) {
      if (this.disableCallTo(data)) {
        return;
      }
      // this.$router.push(`/admin/clinic/consultation/${data.pid}`);
      let params = { pid: data.pid, origin: 'ClinicConsultation', data: { params: { savePage: this.currentPage, saveTab: this.tabs, saveSearch: this.searchText } } }
      this.$router.push({ name: 'ClinicConsultationDetails', params })
    },
    disableCallTo(item) {
      let tag = item.taggings.find((f) => f.tagging === "incoming");
      if (tag) {
        return true;
      }
      return false;
    },
    getStatus(item, forDuration = false) {
      let text = item.status.replace(/_/g, " ");
      if (text === "completed" && forDuration) text = this.$t("total");
      if (text === "checkout") {
        if (forDuration) {
          text = this.$t("waiting");
        } else {
          text = this.$t("dispensary");
        }
      }
      return this.$options.filters.capitalize(text);
    },
    getDuration(item) {
      if (item.status === "completed") {
        return this.convertTime(item.total_duration);
      }
      const end = DateTime.fromISO(this.currentTime);
      let startTime;
      switch (item.status) {
        case "waiting":
          startTime = item.waiting_at;
          break;

        case "serving":
          startTime = item.serving_at;
          break;

        case "checkout":
          startTime = item.checkout_at;
          break;

        case "on_hold":
          startTime = item.on_hold_at;
          break;

        default:
          startTime = new Date();
          break;
      }
      const start = DateTime.fromISO(startTime);

      let diff = end.diff(start, ["hours", "minutes"]);
      let hours = Math.floor(diff.toObject().hours);
      let minutes = Math.floor(diff.toObject().minutes);
      if (hours) {
        return `${hours} ${this.$options.filters.pluralize("hour", {
          count: hours,
        })}  ${minutes} ${this.$options.filters.pluralize("min", { count: minutes })}`;
      }
      return `${minutes < 1 ? "<1" : minutes} ${this.$options.filters.pluralize("min", {
        count: minutes,
      })}`;
    },
    getTodayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = (today.getMonth() + 1).toString().padStart(2, "0");
      const day = today.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    getYesterdayDate() {
      const today = new Date();
      let yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const year = yesterday.getFullYear();
      const month = (yesterday.getMonth() + 1).toString().padStart(2, "0");
      const day = yesterday.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    async onCallPatientIn(data, seat) {
      let payload = {
        seat: seat?.id,
        status: "serving",
      };
      if (this.member.roles.includes("Agent")) {
        payload.provider = this.member.provider;
      }
      this.$axios
        .patch(`/clinics/${this.company.slug}/registrations/${data.pid}`, payload)
        .then(() => {
          this.updateTime();
        })
        .catch((error) => {
          const errorData = error.response?.data?.detail;
          if (errorData && errorData.includes("currently occupied")) {
            this.showMessage(["error", `${seat.name} is currently occupied`]);
          } else {
            this.showMessage([
              "error",
              this.$t("error_message.unable_to_complete_request"),
            ]);
          }
        });
    },
    convertTime(minutes) {
      if (minutes >= 60) {
        let hours = Math.floor(minutes / 60);
        let remainingMinutes = minutes % 60;
        if (hours > 1) {
          hours += " hours ";
        } else if (hours > 0) {
          hours = "1 hour ";
        } else {
          hours = "";
        }
        return (
          hours +
          (remainingMinutes > 1
            ? remainingMinutes + " mins"
            : remainingMinutes > 0
            ? "1 min"
            : "<1 min")
        );
      } else {
        return minutes > 1 ? minutes + " mins" : minutes > 0 ? "1 min" : "<1 min";
      }
    },
    playAudio() {
      this.audio = new Audio(bell);
      this.audio.play();
    },
  },
  beforeDestroy() {
    this.allowRefetch = false;
    this.clearTimer();
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-data-table__wrapper table tbody tr:hover{
    cursor: pointer;
    background: var(--v-neutral200) !important; 
  }
  .table-custom {
    .v-data-table__wrapper {
      overflow: visible !important;
    }
  } 
}
.column-width {
  width: 200px;
}
.v-tooltip__content {
  top: unset !important;
  left: unset !important;
  // margin-top: -45px !important;
  position: absolute;
  bottom: 0;
}
</style>
